import { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate, useParams } from '@tanstack/react-router';
import { ArrowLeft } from 'lucide-react';
import CreateProject from '../../components/Projects/CreateProject';
import UserProfileCard from '../../components/UserProfileCard';
import { getProjectById } from '../../api/Projects';
import type { Project } from '../../utils/interface/project';
import type { ProjectDetails } from '../../utils/interface/project';
import { LayoutGrid } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { queryKeys } from "../../utils/queryKeys";

const ADD_PROJECT_DESCRIPTION =
    'Add project details and related information.';

const EDIT_PROJECT_DESCRIPTION =
    'Update your project details and thumbnail. Changes are saved when you submit the form.';

const VIEW_PROJECT_DESCRIPTION =
    'View project details. All fields are read-only.';

/** Map Project (API/list) to CreateProject initialData shape */
function projectToInitialData(project: Project): ProjectDetails & { id?: string; ifcModel?: string | string[] | { url?: string; name?: string } | null; projectThumbnailPreview?: string | null } {
    return {
        id: project.id,
        name: project.name,
        summary: project.summary ?? '',
        companyName: project.companyName ?? '',
        projectThumbnail: project.projectThumbnail ?? '',
        projectThumbnailPreview: project.projectThumbnailPreview ?? undefined,
        status: project.status,
        files: project.files ?? [],
        userId: project.userId,
        location: project.location ?? '',
        uploadControlPanelDoc: project.uploadControlPanelDoc ?? undefined,
        ifcModel: project.ifcModel ?? undefined,
    };
}

type Mode = 'add' | 'edit' | 'view';

const ProjectFormPage = () => {
    const navigate = useNavigate();
    const { id } = useParams({ from: "/_protected/project-management/edit/$id" });
    const location = useLocation();
    const pathname = location.pathname;
    const [project, setProject] = useState<Project | null>(null);

    const mode: Mode = !id
        ? 'add'
        : pathname.includes('/view/')
            ? 'view'
            : 'edit';

    const shouldFetch = Boolean(id) && mode !== "add";
    const {
        data: fetchedProject,
        isLoading: loading,
        isError: fetchError,
    } = useQuery<Project>({
        queryKey: id ? queryKeys.projects.detail(id) : ["projects", "detail", "missing-id"],
        queryFn: async () => getProjectById(id as string),
        enabled: shouldFetch,
    });

    useEffect(() => {
        if (!shouldFetch) {
            setProject(null);
            return;
        }
        if (fetchedProject) setProject(fetchedProject);
    }, [shouldFetch, fetchedProject]);

    const initialData = project && (mode === 'edit' || mode === 'view')
        ? projectToInitialData(project)
        : undefined;

    if (loading) {
        return (
            <div className="py-24 flex flex-col items-center justify-center min-h-[75vh]">
                <div className="relative">
                    <div className="w-20 h-20 border-[3px] border-[#E761A0]/20 border-t-[#E761A0] rounded-full animate-spin" />
                    <LayoutGrid className="w-8 h-8 text-[#E761A0] absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2" />
                </div>
                <p className="text-white/90 text-base mt-5 font-medium">
                    Loading projects...
                </p>
                <p className="text-gray-500 text-sm mt-1">
                    Fetching your project list
                </p>
            </div>
        );
    }

    if ((mode === 'edit' || mode === 'view') && (!project || fetchError)) {
        return (
            <div className="relative">
                <UserProfileCard />
                <Link
                    to="/project-management"
                    className="inline-flex items-center gap-2 text-[#E761A0] hover:underline font-medium mb-3 text-lg"
                >
                    <ArrowLeft className="w-4 h-4" />
                    Back to Projects
                </Link>
                <p className="text-white/80 text-lg">Project not found. Please go back and open from the list.</p>
            </div>
        );
    }

    const title = mode === 'add' ? 'Add a project' : mode === 'edit' ? 'Edit Project' : 'View Project';
    const description = mode === 'add'
        ? ADD_PROJECT_DESCRIPTION
        : mode === 'edit'
            ? EDIT_PROJECT_DESCRIPTION
            : VIEW_PROJECT_DESCRIPTION;

    return (
        <div className="relative">
            <UserProfileCard />
            <Link
                to="/project-management"
                className="inline-flex items-center gap-2 text-[#E761A0] hover:underline font-medium mb-3 text-lg"
            >
                <ArrowLeft className="w-4 h-4" />
                Back to Projects
            </Link>

            <div className="mb-8">
                <h1 className="text-[34px] lg:text-[38px] xl:text-[56px font-display font-light text-white mb-[15px] leading-[76px]">
                    {title}
                </h1>
                <p className="text-white max-w-[863px] text-lg font-light">
                    {description}
                </p>
            </div>

            <div className="add-project-page-wrapper" data-layout="page">
                <CreateProject
                    layout="page"
                    isEditMode={mode === "edit"}
                    isViewMode={mode === "view"}
                    initialData={initialData}
                    onSuccess={() => navigate({ to: "/project-management" })}
                    refreshProjects={undefined}
                />
            </div>
        </div>
    );
};

export default ProjectFormPage;
